import titleize from "titleize";
import { ageLabel, ageStamp } from "../lib/age";

/**
 * Get a value from an object using a path array or string
 * @param {Object} obj - The object to get the value from
 * @param {Array|String} path - The path to the value, either as an array of keys or a string key
 * @returns {*} The value at the path or undefined if not found
 */
const getNestedValue = (obj, path) => {
  // If path is not an array, just return the direct property
  if (!Array.isArray(path)) {
    return obj[path];
  }
  
  // For array paths, traverse the object
  return path.reduce((current, key) => {
    return current && typeof current === 'object' ? current[key] : undefined;
  }, obj);
};

// A parent row of a list's "Group similar" view: look-alike tasks or
// workflows under one row (`group`, with the members as `_children`). It is
// not a record: it has no reference, no checkbox and no preview.
export const isGroupRow = (data) => !!data?.group;

// How long a group's members have waited, newest to oldest ("1d–9d").
export const groupAgeStamp = (group) => {
  const now = Date.now();
  const newest = ageLabel(now - Date.parse(group.newest_at));
  const oldest = ageLabel(now - Date.parse(group.oldest_at));
  const stamp = document.createElement("span");
  stamp.className = "age-stamp";
  stamp.textContent = newest === oldest ? oldest : `${newest}–${oldest}`;
  stamp.title = `The newest waited ${newest}, the oldest ${oldest}`;
  return stamp;
};

// The Age column: the aging stamp (lib/age.js) right after a row's title,
// in place of the full Created and Updated dates, which stay in the column
// menu. `closed(row)` says whether the work is finished, and `closedAt(row)`
// when, so a closed row keeps the age it had when it closed. Sorting by Age
// sorts by created time, youngest first ascending. A group's parent row
// shows its members' range.
export const ageColumn = ({ closed, closedAt, ...overrides }) => ({
  title: "Age",
  field: "age",
  headerTooltip: "How long since it was created. Hover a stamp for the exact times.",
  width: 96,
  minWidth: 88,
  download: false,
  sorter: (_a, _b, aRow, bRow) => Date.parse(bRow.getData().created_at) - Date.parse(aRow.getData().created_at),
  formatter: (cell) => {
    const row = cell.getRow().getData()
    if (isGroupRow(row)) return groupAgeStamp(row.group)
    return ageStamp({ createdAt: row.created_at, updatedAt: row.updated_at, closed: closed(row), closedAt: closedAt(row) })
  },
  ...overrides,
})

// Tabulator options that let users drag column headers to reorder them and
// remember column order, widths, and visibility in localStorage, keyed by
// persistenceID. Only width/visible are persisted per column (order is
// implicit in the saved layout's sequence) so newly added columns still
// appear, at their original position, for users with saved layouts. A saved
// layout also keeps its old order, so a table whose default order changes
// bumps the version in its persistenceID (`tasks-table-v3`) to show it.
export const columnPersistenceOptions = (persistenceID) => ({
  persistenceID,
  persistence: { columns: ["width", "visible"] },
  movableColumns: true,
});

// Per-row selection checkbox. We deliberately do NOT use Tabulator's built-in
// `rowSelection` cell formatter: it toggles via `row.toggleSelect()`, which is
// gated by `selectableRowsCheck`. These tables set `selectableRowsCheck: () =>
// false` so a *plain row click* never changes the selection (clicking a row only
// opens its preview); selection is driven solely by these checkboxes. So we
// select/deselect programmatically (`row.select()/deselect()`), which bypasses
// that gate, and register the checkbox with the select module so it stays in
// sync when selection changes elsewhere (select-all, Escape-to-clear, arrow-key
// preview nav).
const rowSelectionCheckbox = (cell, { onRangeSelect, rowSelectable } = {}) => {
  if (rowSelectable && !rowSelectable(cell.getRow())) return "";
  const checkbox = document.createElement("input");
  checkbox.type = "checkbox";
  checkbox.setAttribute("aria-label", "Select row");

  const row = cell.getRow();
  checkbox.checked = row.isSelected && row.isSelected();
  let handledRangeSelect = false;

  // Don't let the checkbox click bubble to any row/cell handlers.
  checkbox.addEventListener("click", (e) => {
    e.stopPropagation();
    if (e.shiftKey && onRangeSelect) {
      e.preventDefault();
      handledRangeSelect = true;
      onRangeSelect(row, e);
      setTimeout(() => {
        handledRangeSelect = false;
        checkbox.checked = row.isSelected && row.isSelected();
      });
    }
  });
  checkbox.addEventListener("change", () => {
    if (handledRangeSelect) {
      handledRangeSelect = false;
      return;
    }

    if (row.isSelected()) {
      row.deselect();
    } else {
      row.select();
    }
  });

  cell.getTable().modules?.selectRow?.registerRowSelectCheckbox(row, checkbox);

  return checkbox;
};

// A row-header column of selection checkboxes (with a select-all checkbox in
// the header) for tables that support multi-row selection. Added via Tabulator's
// `rowHeader` option so it stays out of the column-visibility menu, CSV
// downloads, and column persistence. Plain row clicks still open the preview;
// the checkbox is the discoverable way to select rows for bulk actions, instead
// of relying on ctrl/shift row clicks that users don't find. The header keeps the
// built-in `rowSelection` titleFormatter for select-all (it selects rows
// programmatically and isn't subject to the per-row selectability gate). Returns
// a fresh object per call so Tabulator never mutates a definition shared across
// tables. `rowSelectable(row)`, when given, leaves rows that are not records
// (a group's parent row) without a checkbox.
export const rowSelectionRowHeader = ({ onRangeSelect, rowSelectable } = {}) => ({
  formatter: (cell) => rowSelectionCheckbox(cell, { onRangeSelect, rowSelectable }),
  titleFormatter: "rowSelection",
  titleFormatterParams: { rowRange: "active" }, // header toggles the loaded/filtered rows
  headerSort: false,
  headerMenu: false,
  resizable: false,
  frozen: false,
  width: 44,
  minWidth: 44,
  headerHozAlign: "center",
  hozAlign: "center",
  cssClass: "tabulator-row-select",
  // Make the whole gutter cell a hit target, not just the 15px checkbox.
  // Clicks on the input itself are handled by the input (its stopPropagation
  // keeps these from firing); the `input` guard is belt-and-suspenders.
  cellClick: (e, cell) => {
    if (e.target.matches('input[type="checkbox"]')) return
    const row = cell.getRow()
    if (rowSelectable && !rowSelectable(row)) return
    if (e.shiftKey && onRangeSelect) {
      onRangeSelect(row, e)
      return
    }

    if (row.isSelected()) {
      row.deselect()
    } else {
      row.select()
    }
  },
  headerClick: (e, column) => {
    if (e.target.matches('input[type="checkbox"]')) return
    const table = column.getTable()
    if (table.getSelectedRows().length) {
      table.deselectRow()
    } else {
      table.selectRow("active")
    }
  },
});

// Header menu (⋮ in each column header) listing all columns with a
// show/hide toggle, plus a "Reset columns" entry that clears the saved
// layout. Pass via columnDefaults: { headerMenu: columnVisibilityHeaderMenu }.
export const columnVisibilityHeaderMenu = function () {
  const table = this;
  const menu = [];

  for (const column of table.getColumns()) {
    const title = column.getDefinition().title;
    if (!title) continue;

    const label = document.createElement("span");
    label.className = "flex items-center gap-2";

    const check = document.createElement("span");
    check.className = "inline-block w-4 text-center";
    check.textContent = column.isVisible() ? "✓" : "";

    const name = document.createElement("span");
    name.textContent = title;

    label.appendChild(check);
    label.appendChild(name);

    menu.push({
      label,
      action: (e) => {
        e.stopPropagation();
        column.toggle();
        check.textContent = column.isVisible() ? "✓" : "";
      },
    });
  }

  menu.push({ separator: true });

  const resetLabel = document.createElement("span");
  resetLabel.className = "flex items-center gap-2 text-secondary";
  const resetIcon = document.createElement("span");
  resetIcon.className = "inline-block w-4 text-center";
  resetIcon.textContent = "↺";
  const resetText = document.createElement("span");
  resetText.textContent = "Reset columns";
  resetLabel.appendChild(resetIcon);
  resetLabel.appendChild(resetText);

  menu.push({
    label: resetLabel,
    action: (e) => {
      e.stopPropagation();
      const id = table.options && table.options.persistenceID;
      if (id) {
        localStorage.removeItem(`tabulator-${id}-columns`);
      }
      window.location.reload();
    },
  });

  return menu;
};

// The icon a board gets when nobody picks one (PaperworkBoard's column
// default). Repeated down a table it says nothing, so the Board cell leaves it
// out; a board whose icon someone chose keeps it.
const DEFAULT_BOARD_ICON = "📋";

// A board list in a table cell: the board as a softer lead-in and the list's
// name marked in its own colour ("📦 Receiving / Needs review"), the same
// highlighter (.list-mark; ListMarkHelper#list_mark builds it on the server)
// the board's headers and pickers use. Tape is for states, so a list is never
// taped. The mark links to the table filtered to that list. `list` is an
// entry of the tables' lists value ({ name, board_name, board_icon, color });
// an unknown colour is marked in kraft, like a grey list. The board's icon is
// its own element so it can stay when a narrow column squeezes the board's
// name out. `compact` leaves the board's name to the tooltip and keeps only
// its icon and the list ("📦 Needs review"), for a narrow column that would
// otherwise cut both names short. A board on the default icon shows no icon
// at all: the name is in the tooltip, and the list is the mark.
export const boardListMark = (list, href, { compact = false } = {}) => {
  const board = [list.board_icon, list.board_name].filter(Boolean).join(" ");
  const label = board ? `${board}: ${list.name}` : list.name;
  const link = document.createElement("a");
  link.href = href;
  link.className = compact ? "board-mark board-mark--compact no-underline" : "board-mark no-underline";
  link.setAttribute("data-turbo-prefetch", "false");
  link.setAttribute("aria-label", label);
  link.title = label;

  const body = document.createElement("span");
  body.className = "board-mark__body";

  if (board) {
    if (list.board_icon && list.board_icon !== DEFAULT_BOARD_ICON) {
      const icon = document.createElement("span");
      icon.className = "board-mark__icon";
      icon.textContent = list.board_icon;
      body.appendChild(icon);
    }
  }

  if (board && !compact) {
    const boardName = document.createElement("span");
    boardName.className = "board-mark__board";
    boardName.textContent = list.board_name || "";

    const separator = document.createElement("span");
    separator.className = "board-mark__sep";
    separator.setAttribute("aria-hidden", "true");
    separator.textContent = "/";

    body.append(boardName, separator);
  }

  const name = document.createElement("span");
  name.className = "list-mark board-mark__list";
  name.dataset.color = list.color || "gray";
  name.textContent = list.name;
  body.appendChild(name);
  link.appendChild(body);

  return link;
};

// The section-empty slot as a Tabulator placeholder, so an empty page of a
// remote table reads like every other empty section. Pass the copy that is
// true for the current view: a filtered page and an empty log differ. The
// hint is optional: a sentence that says what to do next. `action` is an
// optional { label, href } (or { label, url }) for the text button under the words.
export const emptyStatePlaceholder = (title, hint, action = null) => {
  const slot = document.createElement("div");
  slot.className = "section-empty";

  const copy = document.createElement("div");
  copy.className = "section-empty__copy";

  const heading = document.createElement("p");
  heading.className = "section-empty__title";
  heading.textContent = title;
  copy.appendChild(heading);

  if (hint) {
    const sentence = document.createElement("p");
    sentence.className = "section-empty__hint";
    sentence.textContent = hint;
    copy.appendChild(sentence);
  }

  slot.appendChild(copy);

  const actionHref = action?.href || action?.url;
  if (action?.label && actionHref) {
    const actions = document.createElement("div");
    actions.className = "section-empty__actions";
    const link = document.createElement("a");
    link.className = "btn btn-sm";
    link.href = actionHref;
    link.textContent = action.label;
    actions.appendChild(link);
    slot.appendChild(actions);
  }

  return slot;
};

// Query parameters that change the view without narrowing the list.
const NON_FILTER_PARAMS = new Set([
  "tab", "page", "size", "back", "sort", "dir", "cards", "sql", "share_token",
  "use_meilisearch", "modal_task", "modal_contact", "modal_attachment"
]);

// True when the address narrows the list: any filter or search parameter.
export const viewIsFiltered = (search = window.location.search) => {
  const params = new URLSearchParams(search);
  for (const [key, value] of params.entries()) {
    if (value !== "" && !NON_FILTER_PARAMS.has(key)) return true;
  }
  return false;
};

// A table's empty copy, chosen when it shows: a list that nothing narrows has
// nothing in it ("No tasks yet"); a narrowed list has nothing that matches,
// and says how to widen it. Tabulator calls a function placeholder on render.
// A list that remembers its filters in a cookie and redirects a bare visit back
// to them (tasks) passes `clearsSavedFilters`, so the button asks the server to
// forget them; without it the bare path would bounce straight back.
export const emptyStateFor = (noun, { clearsSavedFilters = false } = {}) => () => (
  viewIsFiltered()
    ? emptyStatePlaceholder("Nothing matches these filters", "Clear a filter or search for something else.", {
      label: "Clear filters",
      href: clearsSavedFilters ? `${window.location.pathname}?clear_filters=true` : window.location.pathname
    })
    : emptyStatePlaceholder(`No ${noun} yet`)
);

export const createAttributeFilterFormatter =(paramFilterName, queryParamValueName, titleizeName = false) => {
  return (cell) => {
    if (!cell.getValue()) {
      return "";
    }

    const data = cell.getRow().getData();
    const value = queryParamValueName ? getNestedValue(data, queryParamValueName) : cell.getValue();

    const currentUrl = new URL(window.location.href);
    currentUrl.searchParams.set(paramFilterName, [value]);
    let name = cell.getValue()
    name = name.replace(/[_-]/g, " ");
    return `<a href="${currentUrl.toString()}" class="link" data-turbo-prefetch="false">${titleizeName ? titleize(name) : cell.getValue(name)}</a>`;
  }
}

// Where a task or workflow stands in a list, as a compact highlight in the
// state's tone (coral when a person must act, sunflower for a wait, lilac
// while the agent works, mint when done, raspberry when it failed, sky for a
// routine open row, kraft for the rest). It sits in the State cell, never
// before a title. `status` is the row's list_status. An open task that needs a
// person other than you is as routine as an open row.
const STATE_TONES = {
  needs_you: "open", needs_person: "idle",
  idle: "idle",
  waiting_contact: "wait", on_hold: "wait",
  running: "you", retrying: "bad",
  completed: "done", agent_done: "done",
  rejected: "bad", failed: "bad",
}

// The highlight for a list row's State cell, or null when the row has no status.
export const stateBadge = (status, options = {}) => {
  if (!status) return null
  return stateMark(status, options)
}

export const stateMark = (status, { href, title } = {}) => {
  if (!status) return ""
  const mark = document.createElement(href ? "a" : "span")
  mark.className = "state-mark no-underline"
  if (href) {
    mark.href = href
    mark.dataset.turboPrefetch = "false"
  }
  mark.dataset.tone = STATE_TONES[status.key] || "off"
  mark.dataset.state = status.key
  mark.title = title || status.label
  mark.textContent = status.label
  return mark
}
